import {PropsWithChildren, useCallback, useEffect, useRef} from 'react';

import c from './index.less';

interface Props extends PropsWithChildren {
    sparkColor?: string;
    sparkSize?: number;
    sparkRadius?: number;
    sparkCount?: number;
    duration?: number;
    easing?: 'linear' | 'ease-in' | 'ease-in-out' | 'ease-out';
    extraScale?: number;
}

interface Spark {
    x: number;
    y: number;
    angle: number;
    startTime: number;
}

export default function ClickSpark({
    sparkColor = '#fff',
    sparkSize = 10,
    sparkRadius = 15,
    sparkCount = 8,
    duration = 400,
    easing = 'ease-out',
    extraScale = 1,
    children,
}: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const sparksRef = useRef<Spark[]>([]);

    useEffect(
        () => {
            const canvas = canvasRef.current;
            const parent = canvas?.parentElement;
            if (!canvas || !parent) {
                return;
            }

            let resizeTimeout = 0;
            const resizeCanvas = () => {
                const {width, height} = parent.getBoundingClientRect();
                if (canvas.width !== width || canvas.height !== height) {
                    canvas.width = width;
                    canvas.height = height;
                }
            };
            const handleResize = () => {
                window.clearTimeout(resizeTimeout);
                resizeTimeout = window.setTimeout(resizeCanvas, 100);
            };
            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(parent);
            resizeCanvas();

            return () => {
                resizeObserver.disconnect();
                window.clearTimeout(resizeTimeout);
            };
        },
        []
    );

    const ease = useCallback(
        (progress: number) => {
            switch (easing) {
                case 'linear':
                    return progress;
                case 'ease-in':
                    return progress * progress;
                case 'ease-in-out':
                    return progress < 0.5
                        ? 2 * progress * progress
                        : -1 + (4 - 2 * progress) * progress;
                default:
                    return progress * (2 - progress);
            }
        },
        [easing]
    );

    useEffect(
        () => {
            const canvas = canvasRef.current;
            const context = canvas?.getContext('2d');
            if (!canvas || !context) {
                return;
            }

            let animationId = 0;
            const draw = (timestamp: number) => {
                context.clearRect(0, 0, canvas.width, canvas.height);
                sparksRef.current = sparksRef.current.filter(spark => {
                    const elapsed = timestamp - spark.startTime;
                    if (elapsed >= duration) {
                        return false;
                    }

                    const eased = ease(elapsed / duration);
                    const distance = eased * sparkRadius * extraScale;
                    const lineLength = sparkSize * (1 - eased);
                    const cosine = Math.cos(spark.angle);
                    const sine = Math.sin(spark.angle);

                    context.strokeStyle = sparkColor;
                    context.lineWidth = 2;
                    context.beginPath();
                    context.moveTo(spark.x + distance * cosine, spark.y + distance * sine);
                    context.lineTo(
                        spark.x + (distance + lineLength) * cosine,
                        spark.y + (distance + lineLength) * sine
                    );
                    context.stroke();
                    return true;
                });
                animationId = requestAnimationFrame(draw);
            };

            animationId = requestAnimationFrame(draw);
            return () => cancelAnimationFrame(animationId);
        },
        [duration, ease, extraScale, sparkColor, sparkRadius, sparkSize]
    );

    const handleClick: React.MouseEventHandler<HTMLDivElement> = event => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const rect = canvas.getBoundingClientRect();
        const now = performance.now();
        const sparks = Array.from({length: sparkCount}, (_, index) => ({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
            angle: 2 * Math.PI * index / sparkCount,
            startTime: now,
        }));
        sparksRef.current.push(...sparks);
    };

    return (
        <div className={c.root} onClick={handleClick}>
            <canvas ref={canvasRef} aria-hidden="true" className={c.canvas} />
            {children}
        </div>
    );
}
